import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { HtmlSanitizerService } from 'src/app/core/services/html-sanitizer.service';
import { XdsChatBubbleComponent } from 'src/app/shared/ui/chat-bubble/chat-bubble.component';
import { HelpSource } from '../../models/help-chat.models';
import { renderChatMarkdown } from './chat-markdown';

/**
 * Eine Chat-Bubble (User oder Assistant). Rendert Markdown-Body,
 * routet inline-Links ueber den Output `linkClick`. Die A11y-Attribute
 * (role=status, aria-live=polite) liegen auf der Assistant-Bubble,
 * damit Screenreader neue Antworten automatisch vorlesen.
 */
@Component({
  selector: 'app-help-message-bubble',
  standalone: true,
  imports: [CommonModule, XdsChatBubbleComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <xds-chat-bubble
      [role]="role"
      avatarName="Assistent"
      [avatarIcon]="role === 'assistant' ? 'fa-solid fa-sparkles' : undefined"
      [avatarStatus]="role === 'assistant' ? 'ai' : 'primary'"
      [attr.role]="role === 'assistant' ? 'status' : null"
      [attr.aria-live]="role === 'assistant' ? 'polite' : null"
    >
      <div class="bubble__content" (click)="onContentClick($event)" [innerHTML]="rendered"></div>
      <ng-content></ng-content>
    </xds-chat-bubble>
  `,
  styles: [
    `
      .bubble__content {
        font-size: inherit;
      }
      .bubble__content :where(strong, b) {
        font-weight: 600;
      }
      .bubble__content :where(ol, ul) {
        padding-left: 1.2em;
        margin: 4px 0;
      }
      .bubble__content :where(li) {
        margin: 2px 0;
      }
      /* WARUM (2026-05-29): Die Links kommen via [innerHTML] -> Emulated Encapsulation scoped
         eine normale '.bubble__content a'-Regel weg (kein _ngcontent-Attribut am eingefuegten
         <a>), die Links fielen daher auf einen globalen Anchor-Style zurueck (uneinheitliches,
         z.T. kontrastarmes Blau). Mit ::ng-deep (unter .bubble__content gescoped) stylen wir sie
         gezielt: kraeftiges, lesbares Brand-Blau, dezenter Unterstrich mit Abstand, klarer Hover.
         Alle Farben kommen aus den echten XDS-Token-Rollen (--action-*, --tint-*, --text-*) —
         die sind bereits Light/Dark-aware, ein eigener :host-context(.theme-dark)-Zweig ist
         dafuer nicht mehr noetig (frueher: hartkodierte --clr-primary-100-Duplikate). */
      .bubble__content ::ng-deep a {
        color: var(--action-primary);
        font-weight: 600;
        text-decoration: underline;
        text-decoration-thickness: 1px;
        text-decoration-color: var(--tint-primary-border);
        text-underline-offset: 2px;
        border-radius: 3px;
        padding: 0 1px;
        cursor: pointer;
        transition: color 0.15s ease, text-decoration-color 0.15s ease, background 0.15s ease;
      }
      .bubble__content ::ng-deep a:hover {
        color: var(--action-primary-hover);
        text-decoration-color: currentColor;
        background: var(--tint-primary-bg);
      }

      /* Kein User-Bubble-Kontrast-Override mehr noetig: xds-chat-bubble nutzt fuer
         die User-Variante einen HELLEN Tint (--tint-primary-bg/-text, analog
         xds-chip.is-active), keine solide dunkle Flaeche mehr — die Standard-
         Link-/cm-*-Farben (dunkel auf hell) passen jetzt fuer beide Rollen. */

      /* =====================================================================
         Struktur-Renderer (chat-markdown.ts) — AUSSCHLIESSLICH Blau/Neutral.
         Dringlichkeit ueber Fettung + gefuelltes-vs-umrandetes blaues Chip +
         ⚠-Glyph; keine zweite Farbe. Scoped via ::ng-deep, weil der Inhalt per
         [innerHTML] injiziert wird (kein _ngcontent an den Knoten).
      ===================================================================== */
      .bubble__content ::ng-deep .cm-head {
        display: flex;
        align-items: baseline;
        flex-wrap: wrap;
        gap: 7px;
        margin: 11px 0 4px;
        font-size: 13.5px;
        font-weight: 700;
        color: var(--xds-text);
      }
      .bubble__content ::ng-deep .cm-head--top {
        font-size: 14.5px;
      }
      .bubble__content ::ng-deep .cm-head:first-child {
        margin-top: 0;
      }
      .bubble__content ::ng-deep .cm-count {
        font-size: 11px;
        font-weight: 700;
        line-height: 1.7;
        padding: 0 8px;
        border-radius: 999px;
        font-variant-numeric: tabular-nums;
        background: var(--tint-primary-bg);
        color: var(--action-primary);
      }
      .bubble__content ::ng-deep .cm-note {
        font-size: 11.5px;
        font-weight: 500;
        color: var(--text-subtle);
      }
      .bubble__content ::ng-deep .cm-subhead {
        margin: 9px 0 3px;
        font-size: 12.5px;
        font-weight: 600;
        color: var(--text-muted);
      }
      .bubble__content ::ng-deep ul.cm-rows {
        list-style: none;
        margin: 3px 0 6px;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 1px;
      }
      .bubble__content ::ng-deep ul.cm-rows li {
        display: flex;
        align-items: baseline;
        gap: 8px;
        padding: 3px 5px;
        border-radius: 7px;
      }
      .bubble__content ::ng-deep .cm-dot {
        flex: 0 0 auto;
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background: var(--tint-primary-border);
        transform: translateY(-2px);
      }
      .bubble__content ::ng-deep .cm-main {
        flex: 1;
        min-width: 0;
      }
      .bubble__content ::ng-deep .cm-name {
        font-weight: 600;
        color: var(--xds-text);
      }
      .bubble__content ::ng-deep .cm-meta {
        color: var(--text-subtle);
        font-size: 12.5px;
      }
      .bubble__content ::ng-deep .cm-chip {
        flex: 0 0 auto;
        align-self: center;
        font-size: 10.5px;
        font-weight: 700;
        line-height: 1.6;
        padding: 0 8px;
        border-radius: 999px;
        white-space: nowrap;
      }
      .bubble__content ::ng-deep .cm-chip--urgent {
        background: var(--action-primary);
        color: var(--text-on-primary);
        text-transform: capitalize;
      }
      .bubble__content ::ng-deep .cm-chip--high {
        background: transparent;
        color: var(--action-primary);
        box-shadow: inset 0 0 0 1px var(--tint-primary-border);
        text-transform: capitalize;
      }
      .bubble__content ::ng-deep .cm-chip--date {
        background: var(--surface-3);
        color: var(--text-muted);
        font-weight: 600;
      }
      .bubble__content ::ng-deep .cm-tag {
        font-size: 11px;
        color: var(--text-subtle);
      }
      .bubble__content ::ng-deep .cm-warn {
        font-weight: 600;
        color: var(--xds-text);
      }
      .bubble__content ::ng-deep ol.cm-rank {
        list-style: none;
        margin: 3px 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
      }
      .bubble__content ::ng-deep ol.cm-rank li {
        display: flex;
        align-items: center;
        gap: 9px;
        padding: 2px 3px;
      }
      .bubble__content ::ng-deep .cm-rank-no {
        flex: 0 0 auto;
        width: 19px;
        height: 19px;
        display: grid;
        place-items: center;
        border-radius: 6px;
        font-size: 11px;
        font-weight: 700;
        background: var(--tint-primary-bg);
        color: var(--action-primary);
      }
      .bubble__content ::ng-deep .cm-rank-name {
        flex: 1;
        font-weight: 600;
        color: var(--xds-text);
      }
      .bubble__content ::ng-deep .cm-rank-val {
        font-variant-numeric: tabular-nums;
        font-weight: 600;
        color: var(--text-muted);
      }
      .bubble__content ::ng-deep .cm-more {
        font-size: 12px;
        color: var(--text-subtle);
        padding: 2px 5px;
      }
      .bubble__content ::ng-deep .cm-foot {
        margin-top: 5px;
        padding-top: 5px;
        font-size: 12px;
        color: var(--text-subtle);
        border-top: 1px solid var(--xds-border);
      }
    `,
  ],
})
export class HelpMessageBubbleComponent {
  @Input() role: 'user' | 'assistant' = 'assistant';
  @Input() set content(value: string) {
    this._content = value ?? '';
    this.rendered = renderChatMarkdown(this._content);
  }
  get content(): string {
    return this._content;
  }
  @Input() sources: HelpSource[] = [];

  /** Emit bei Klick auf einen inline-Link mit dem href-Wert (z.B. "/planner" oder "tour:terminal"). */
  @Output() linkClick = new EventEmitter<string>();

  protected rendered: string = '';
  private _content = '';

  constructor(private sanitizer: HtmlSanitizerService) {}

  protected onContentClick(ev: MouseEvent): void {
    const target = ev.target as HTMLElement;
    const anchor = target?.closest?.('a');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    if (!href) return;
    ev.preventDefault();
    this.linkClick.emit(href);
  }

}
